// src/components/Auth/EmailConfirmationSent.js
import React, { useContext, useEffect, useState } from 'react';
import { Alert, Container, Row, Col } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { broadcastCartSync } from '../Utils/cartSync';
import { resendEmailVerification, getCurrentUser } from '../../services/api';
import useNotification from '../../hooks/useNotification';
import '../../styles/login.css';

const EmailConfirmationSent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hydrated, user } = useContext(AuthContext);
  const { showNotification } = useNotification();
  
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // Check if user came from checkout flow
  const fromCheckout = searchParams.get('from') === 'checkout';
  const redirectAfterVerification = searchParams.get('redirect') || '/';

  useEffect(() => {
    broadcastCartSync();
    
    // Store the redirect URL in localStorage so it persists through email verification
    if (fromCheckout) {
      localStorage.setItem('postVerificationRedirect', redirectAfterVerification);
    }
  }, [fromCheckout, redirectAfterVerification]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendEmail = async () => {
    // Try to get email from user object first, then fallback to localStorage
    let userEmail = user?.email;
    
    if (!userEmail) {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
          const parsedUser = JSON.parse(storedUser);
          userEmail = parsedUser?.email;
          console.log('[EmailConfirmationSent] Retrieved email from localStorage:', userEmail);
        }
      } catch (error) {
        console.error('[EmailConfirmationSent] Error parsing stored user:', error);
      }
    }
    
    // Final fallback: if we have an auth token but no email, try to fetch current user
    if (!userEmail && localStorage.getItem('authToken')) {
      console.log('[EmailConfirmationSent] No email found locally, but auth token present. Fetching current user...');
      try {
        const currentUser = await getCurrentUser();
        console.log('[EmailConfirmationSent] API response:', currentUser);
        
        // The API response has email directly, not nested in user object
        userEmail = currentUser?.email || currentUser?.user?.email;
        console.log('[EmailConfirmationSent] Extracted email from API:', userEmail);
      } catch (apiError) {
        console.error('[EmailConfirmationSent] Failed to fetch current user:', apiError);
      }
    }
    
    if (!userEmail) {
      console.error('[EmailConfirmationSent] No email found in user object or localStorage');
      console.log('[EmailConfirmationSent] Available data:', {
        user,
        storedUser: localStorage.getItem('user'),
        authToken: localStorage.getItem('authToken') ? 'present' : 'missing'
      });
      showNotification('Unable to resend email. Please try logging in again.', 'error');
      return;
    }

    setIsResending(true);
    try {
      console.log('[EmailConfirmationSent] Sending verification email to:', userEmail);
      await resendEmailVerification(userEmail);
      showNotification('Verification email sent! Please check your inbox.', 'success');
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      console.error('Failed to resend email:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      
      let errorMessage = 'Failed to resend email. Please try again later.';
      if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = `Network error: ${error.message}`;
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setIsResending(false);
    }
  };

  const handleBackClick = () => {
    if (fromCheckout) {
      navigate('/'); // Go to home if they came from checkout
    } else {
      navigate('/login'); // Original behavior for signup flow
    }
  };

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col md={6}>
          <div className="login-form text-center">
            <h2>Email Verification Required</h2>
            
            {fromCheckout && (
              <Alert variant="warning" className="mb-3">
                <strong>Oops — just one more step!</strong> Please verify your email before completing checkout.
              </Alert>
            )}
            
            <div className="confirmation-message mb-4">
              <p>
                {fromCheckout 
                  ? "We need to verify your email address before you can place an order." 
                  : "Thanks for signing up! We've sent a confirmation email to your inbox."
                }
              </p>
              <p>
                Please click the link in that email to verify your address.
                <br />
                <small className="text-muted">If you don't see it, be sure to check your spam or junk folder.</small>
              </p>
              
              {user?.email && (
                <p className="mt-3">
                  <small className="text-muted">Sent to: <strong>{user.email}</strong></small>
                </p>
              )}
            </div>

            <div className="d-grid gap-2">
              <button
                type="button"
                className="ff-btn ff-btn--secondary mb-2"
                onClick={handleResendEmail}
                disabled={isResending || resendCooldown > 0}
              >
                {isResending 
                  ? 'Sending...' 
                  : resendCooldown > 0 
                    ? `Resend in ${resendCooldown}s` 
                    : 'Resend Verification Email'
                }
              </button>

              <button
                type="button"
                className="ff-btn ff-btn--secondary"
                onClick={handleBackClick}
              >
                {fromCheckout ? 'Continue Shopping' : 'Back to Login'}
              </button>
            </div>

            {fromCheckout && (
              <div className="mt-3">
                <small className="text-muted">
                  Once verified, you can return to checkout to complete your order.
                </small>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default EmailConfirmationSent;